import { deleteDta, fiduciarieMessages, useAppDispatch } from 'azshared';
import type { DtaListedAccount } from 'azshared';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ModalDialog } from '../../../components/modal/modal-dialog';
import { useGlobalActions } from '../../../hooks/global-actions';

interface FiduciarieDeleteModalProps {
  // The account whose delete is waiting for the confirmation: the modal is hidden without one.
  account?: DtaListedAccount;
  onClose: () => void;
  onDeleted: () => void;
}

// Asks to confirm the delete, then deletes the account and reports the outcome with a toast.
export const FiduciarieDeleteModal = (props: FiduciarieDeleteModalProps) => {
  const { account, onClose, onDeleted } = props;
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { showToast, closeToast } = useGlobalActions();

  const handleConfirm = useCallback(
    (accountToDelete: DtaListedAccount) => {
      onClose();
      dispatch(deleteDta(accountToDelete.username))
        .unwrap()
        .then(() => {
          showToast({
            type: 'success',
            title: formatMessage(fiduciarieMessages.deleteSuccess),
            close: closeToast
          });
          onDeleted();
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: formatMessage(fiduciarieMessages.deleteError),
            close: closeToast
          });
        });
    },
    [dispatch, onClose, onDeleted, formatMessage, showToast, closeToast]
  );

  if (!account) {
    return null;
  }

  return (
    <ModalDialog
      open
      title={formatMessage(fiduciarieMessages.deleteConfirmTitle)}
      subtitle={formatMessage(fiduciarieMessages.deleteConfirmSubtitle, {
        username: account.username,
        trustCompany: account.trustCompany.name
      })}
      cancelButtonLabel={formatMessage(fiduciarieMessages.buttonCancel)}
      confirmButtonLabel={formatMessage(fiduciarieMessages.buttonConfirm)}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={() => handleConfirm(account)}
    />
  );
};
